import { useAppDispatch, useAppSelector } from '@/app/hooks'
import type { BookName } from '@/features/book/bookApi'
import { makeSelectChapter } from '@/features/book/bookSlice'
import {
  clearSelectedVerses,
  makeChapterVersesSelector,
} from '@/features/choosen/choosenSlice'
import {
  _T,
  selectAfterNavigateKeepSelection,
  selectShowComments,
  setAfterNavigateKeepSelection,
  useColorPalette,
  useLanguage,
  useT,
  useUiSizeClassName,
} from '@/features/settings/settingsSlice'
import { copyToClipboard, getSelectedVersesText } from '@/utils/copy-utils'
import { ActionBar, Button, Portal, Checkbox } from '@chakra-ui/react'
import { useCallback, useEffect } from 'react'
import { FaRegCopy } from 'react-icons/fa'
import { useParams } from 'react-router-dom'
import { toaster, Toaster } from '../ui/toaster'
import { Tooltip } from '../ui/tooltip'
import { selectIsJumpToDialogOpen } from '@/features/status/statusSlice'
import { MdClear } from 'react-icons/md'

type Props = {}

export default function VerseActionBar({}: Props) {
  const dispatch = useAppDispatch()
  const { book, chapter } = useParams<{ book: string; chapter?: string }>()
  const language = useLanguage()
  const chapterVersesSelector = makeChapterVersesSelector(
    book!,
    parseInt(chapter || '1'),
  )
  const selectedVerses = useAppSelector(chapterVersesSelector)
  const chapterData = useAppSelector(
    makeSelectChapter(book as BookName, parseInt(chapter || '1')),
  ).contents!.nodeData
  const isJumpToDialogOpen = useAppSelector(selectIsJumpToDialogOpen)
  const afterNavigateKeepSelection = useAppSelector(
    selectAfterNavigateKeepSelection,
  )
  const showComments = useAppSelector(selectShowComments)

  const handleCopy = useCallback(
    (force: boolean = false) => {
      const selection = window.getSelection()
      if (
        !force &&
        selection &&
        selection.type === 'Range' &&
        selection.toString().length > 0
      ) {
        return
      }

      if (!selectedVerses.length) return

      copyToClipboard(
        getSelectedVersesText(
          language,
          book as BookName,
          parseInt(chapter || '1'),
          selectedVerses,
          chapterData,
        ),
        showComments,
      )
        .then(() => {
          toaster.create({
            title: _T(
              [
                `已复制 ${selectedVerses.length} 节经文`,
                `已複製 ${selectedVerses.length} 節經文`,
              ],
              language,
            ),
            type: 'success',
          })
        })
        .catch(() => {
          toaster.create({
            title: _T(['复制失败', '複製失敗'], language),
            type: 'error',
          })
        })
      dispatch(
        clearSelectedVerses({
          book: book!,
          chapter: parseInt(chapter || '1'),
        }),
      )
    },
    [
      language,
      book,
      chapter,
      selectedVerses,
      chapterData,
      dispatch,
      showComments,
    ],
  )

  const handleCancel = useCallback(() => {
    if (!selectedVerses.length) return
    toaster.create({
      title: _T(
        [
          `已取消 ${selectedVerses.length} 节经文`,
          `已取消 ${selectedVerses.length} 節經文`,
        ],
        language,
      ),
      type: 'success',
    })
    dispatch(
      clearSelectedVerses({
        book: book!,
        chapter: parseInt(chapter || '1'),
      }),
    )
  }, [book, chapter, dispatch, language, selectedVerses.length])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isJumpToDialogOpen) return
      if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
        handleCopy()
      } else if (event.key === 'Escape') {
        handleCancel()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    language,
    book,
    chapter,
    selectedVerses,
    chapterData,
    dispatch,
    handleCopy,
    handleCancel,
    isJumpToDialogOpen,
  ])

  return (
    <>
      <ActionBar.Root open={selectedVerses.length > 0}>
        <Portal>
          <ActionBar.Positioner>
            <ActionBar.Content bgColor={`${useColorPalette()}.500`}>
              <ActionBar.SelectionTrigger
                color="white"
                fontSize={useUiSizeClassName('sm', 'button')}
              >
                {useT(['已选', '已選'])} {selectedVerses.length}
              </ActionBar.SelectionTrigger>

              <Tooltip
                showArrow
                content={useT([
                  '复制选中经文 ' +
                    (/Macintosh|Mac OS/.test(navigator.userAgent)
                      ? 'Cmd+C'
                      : 'Ctrl+C'),
                  '復制選中經文 ' +
                    (/Macintosh|Mac OS/.test(navigator.userAgent)
                      ? 'Cmd+C'
                      : 'Ctrl+C'),
                ])}
                contentProps={{
                  css: {
                    '--tooltip-bg': 'white',
                    color: 'black',
                    letterSpacing: '0.15em',
                  },
                }}
              >
                <Button
                  variant="outline"
                  // @ts-ignore
                  size={useUiSizeClassName('sm', 'button')}
                  onClick={() => handleCopy(true)}
                  color="white"
                  _hover={{
                    backgroundColor: `${useColorPalette()}.400`,
                  }}
                >
                  <FaRegCopy />
                </Button>
              </Tooltip>
              <Tooltip
                showArrow
                content={useT(['清除选中经文 Esc', '清除選中經文 Esc'])}
                contentProps={{
                  css: {
                    '--tooltip-bg': 'white',
                    color: 'black',
                    letterSpacing: '0.15em',
                  },
                }}
              >
                <Button
                  variant="outline"
                  // @ts-ignore
                  size={useUiSizeClassName('sm', 'button')}
                  onClick={handleCancel}
                  color="white"
                  _hover={{
                    backgroundColor: `${useColorPalette()}.400`,
                  }}
                >
                  <MdClear />
                </Button>
              </Tooltip>

              <Checkbox.Root
                variant={'outline'}
                checked={afterNavigateKeepSelection}
                onCheckedChange={({ checked }) => {
                  dispatch(setAfterNavigateKeepSelection(!!checked))
                }}
                color="white"
                // @ts-ignore
                size={useUiSizeClassName('sm', 'control')}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control color="white" borderColor={'white'} />
                <Tooltip
                  showArrow
                  content={useT([
                    '当导航离开本页时返回仍然选中',
                    '當導航離開本頁時返回仍然選中',
                  ])}
                >
                  <Checkbox.Label>{useT(['保持', '保持'])}</Checkbox.Label>
                </Tooltip>
              </Checkbox.Root>
            </ActionBar.Content>
          </ActionBar.Positioner>
        </Portal>
      </ActionBar.Root>
      <Toaster />
    </>
  )
}
